import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import { useMenu, useRestaurantCart } from '@happyspace/sdk/restaurant';
import MenuItemCard from '../components/MenuItemCard';
import SectionHeading from '../components/SectionHeading';

export default function Menu() {
  const { items, categories, loading } = useMenu();
  const { addItem } = useRestaurantCart();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const matchesCategory = activeCategory === 'all' || item.categoryId === activeCategory;
      const matchesSearch = !search || item.title.toLowerCase().includes(search.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [items, activeCategory, search]);

  return (
    <>
      <Helmet>
        <title>Biryani Menu — Dum-Cooked Dosas, Biryanis & More | Omaha, Council Bluffs</title>
        <meta name="description" content="Browse Biryani2Go's full menu: handmade biryani, dosas, samosas & sides. Fresh dum-cooked, made-to-order. Pickup & delivery near me in Omaha & Council Bluffs. Open now." />
        <meta name="keywords" content="biryani menu, dosa, samosa, Indian food near me, restaurant near me open now, restaurant near me takeout, restaurant near me open, Omaha takeout" />
        <link rel="canonical" href="https://biryani2go.us/menu" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `
          {
            "@context": "https://schema.org",
            "@type": "RestaurantMenu",
            "restaurant": {
              "@type": "Restaurant",
              "name": "Biryani2Go",
              "url": "https://biryani2go.us",
              "cuisine": "Indian"
            },
            "hasMenuSection": [
              {
                "@type": "MenuSection",
                "name": "Biryani",
                "description": "Dum-sealed, hand-layered biryani with fragrant basmati rice"
              },
              {
                "@type": "MenuSection",
                "name": "Dosas",
                "description": "Crispy rice-and-lentil crepes, fresh off the griddle"
              },
              {
                "@type": "MenuSection",
                "name": "Samosas & Appetizers",
                "description": "Made-to-order fried and baked appetizers"
              }
            ]
          }
        ` }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://biryani2go.us"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Menu",
                "item": "https://biryani2go.us/menu"
              }
            ]
          }
        ` }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Is Biryani2Go open now near me in Omaha?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, Biryani2Go serves Omaha and Council Bluffs for pickup and delivery. Check the menu page for real-time availability, or visit our contact page for hours and location details."
                }
              },
              {
                "@type": "Question",
                "name": "What biryani restaurant has the best delivery near me?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Biryani2Go offers fresh dum-cooked biryani delivered hot to your door in Omaha and Council Bluffs. Every order is sealed and steamed to order, never pre-made."
                }
              },
              {
                "@type": "Question",
                "name": "Can I order biryani for takeout?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Biryani2Go accepts pickup orders for all items on the menu. Orders are ready in 30–45 minutes and hand-packed to travel hot."
                }
              }
            ]
          }
        ` }} />
      </Helmet>

      <section className="bg-maroon py-16 md:py-20">
        <div className="max-w-screen-xl mx-auto px-5 md:px-8">
          <SectionHeading
            eyebrow="The Full Spread"
            title="Biryani, Dosas, Samosa & More — Full Menu"
            description="Every dish is made to order — dum-sealed and steamed fresh. Add what you love to your dabba."
          />
        </div>
      </section>

      <section className="bg-surface py-12 md:py-16">
        <div className="max-w-screen-md mx-auto px-5 md:px-8 space-y-6">
          <div>
            <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon mb-4">Why Order Biryani From Biryani2Go?</h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-2 mb-4">
              Fresh dum-cooked biryani, made to order — never pre-made, always sealed in a handi and steamed slow until every grain of basmati absorbs the spices. Available for pickup or delivery across Omaha and Council Bluffs. Whether you're hunting for biryani near me, dosas for breakfast, or samosas to share, every item on this menu goes through the same patient dum cooking that defines real Indian rice dishes.
            </p>
          </div>
          <div>
            <h3 className="font-display font-semibold text-xl text-maroon mb-3">Quick Questions</h3>
            <div className="space-y-3 text-sm md:text-base text-ink-2">
              <div>
                <p className="font-semibold text-maroon mb-1">What's the difference between our biryanis?</p>
                <p>Each biryani is built on fragrant basmati rice, hand-layered with marinated meat (or vegetables for veg biryanis), sealed with dough, and steamed for hours. The differences come down to meat choice, spice blend, and regional style.</p>
              </div>
              <div>
                <p className="font-semibold text-maroon mb-1">Can I customize my order?</p>
                <p>Yes! We encourage customization — adjust spice levels, swap sides, or add extras. Just note in the order or <a href="/contact" className="text-ember font-semibold underline">reach out to us</a> for larger modifications.</p>
              </div>
              <div>
                <p className="font-semibold text-maroon mb-1">How long for pickup vs. delivery?</p>
                <p>Pickup is typically ready in 30–45 minutes; delivery adds another 15–30 depending on distance. Times are estimated at checkout. We hand-pack every order to ensure it stays hot on the way.</p>
              </div>
            </div>
          </div>
          <p className="text-sm text-ink-2/70 italic">
            For more on Indian food, dum cooking, and what to order, check out <a href="/blog" className="text-ember underline">our blog</a>.
          </p>
        </div>
      </section>

      <section className="bg-surface py-10 md:py-12 sticky top-20 z-30 border-b border-line shadow-sm">
        <div className="max-w-screen-xl mx-auto px-5 md:px-8 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="flex gap-2 overflow-x-auto pb-1 md:pb-0 -mx-1 px-1">
            <button
              onClick={() => setActiveCategory('all')}
              className={`shrink-0 px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                activeCategory === 'all' ? 'bg-ember text-white' : 'bg-surface2 text-ink-2 hover:bg-line'
              }`}
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat._id}
                onClick={() => setActiveCategory(cat._id)}
                className={`shrink-0 px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  activeCategory === cat._id ? 'bg-ember text-white' : 'bg-surface2 text-ink-2 hover:bg-line'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
          <div className="relative md:w-72 shrink-0">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search the menu..."
              className="field pl-11"
            />
          </div>
        </div>
      </section>

      <section className="bg-surface py-14 md:py-20 min-h-[40vh]">
        <div className="max-w-screen-xl mx-auto px-5 md:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="card-tin h-96 animate-pulse bg-surface2" />
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((item, i) => (
                <MenuItemCard key={item._id} item={item} onAdd={(it) => addItem(it)} index={i} />
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="card-tin p-16 text-center max-w-xl mx-auto"
            >
              <p className="font-display text-2xl text-maroon mb-2">Menu coming soon</p>
              <p className="text-ink-2">
                We're finalizing our dabbas. Check back shortly, or reach out on the Contact page for updates.
              </p>
            </motion.div>
          )}
        </div>
      </section>
    </>
  );
}
